import React, { ReactElement } from 'react';
import { MoreOutlined } from '@ant-design/icons';
import { MenuProps } from 'antd';
import { Trans } from 'react-i18next';
import { Button } from 'common/components/antd/Button/Button';
import { Dropdown } from 'common/components/antd/Dropdown/Dropdown';
import { ColumnsType } from 'common/components/antd/Table/Table';
import { Tag } from 'common/components/antd/Tag/Tag';
import { Centered } from 'common/components/Utilities/Centered';
import { EEmissionAccountColumnKeys } from 'domains/accounts/constants/accounts.constants';
import { RenderGUID, RenderIBAN } from 'domains/accounts/utils/render-account-iban';
import { TEmissionAccountRecord } from '../../counterparty-emission/types/counterparty-emission.types';
import ActualBalanceComponent from '../components/ActualBalance/ActualBalance.component';
import { SStatusCell } from '../counterparty-processing.page.style';
import { EmissionAccountRecord, ItemClickEventHandler } from '../types/counterparty-processing.types';

export const getColumns = ({
  menuItems,
  onItemActionClick,
}: {
  menuItems?: MenuProps['items'];
  onItemActionClick?: ItemClickEventHandler;
} = {}): ColumnsType<EmissionAccountRecord> => [
  {
    title: <Centered>#</Centered>,
    width: 60,
    key: EEmissionAccountColumnKeys.ID,
    dataIndex: EEmissionAccountColumnKeys.ID,
    render: (value) => <Centered>{value}</Centered>,
  },
  {
    title: <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.accountNumber" />,
    width: 234,
    key: EEmissionAccountColumnKeys.WalletAccountGuid,
    dataIndex: EEmissionAccountColumnKeys.WalletAccountGuid,
    render: (value, record, index): ReactElement =>
      <RenderGUID value={value} index={index} />,
  },
  {
    title: <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.iban" />,
    width: 234,
    key: EEmissionAccountColumnKeys.IBAN,
    dataIndex: EEmissionAccountColumnKeys.IBAN,
    render: (value, record, index): ReactElement =>
      <RenderIBAN value={value} index={index} />,
  },
  // ...(IS_ADMIN_FE
  //   ? [
  //       {
  //         title: <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.stellarAddress" />,
  //         width: 234,
  //         key: EEmissionAccountColumnKeys.StellarAddress,
  //         dataIndex: EEmissionAccountColumnKeys.StellarAddress,
  //         render: renderStellarAddress,
  //       },
  //     ]
  //   : []),
  {
    title: <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.currency" />,
    width: 179,
    key: EEmissionAccountColumnKeys.Currency,
    dataIndex: EEmissionAccountColumnKeys.Currency,
    render: (value) => <Tag>{value}</Tag>,
  },
  {
    title: <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.branch" />,
    width: 179,
    key: EEmissionAccountColumnKeys.Branch,
    dataIndex: EEmissionAccountColumnKeys.Branch,
  },
  {
    title: <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.actualBalance" />,
    width: 170,
    key: EEmissionAccountColumnKeys.ActualBalance,
    dataIndex: EEmissionAccountColumnKeys.ActualBalance,
    render: (value, record: TEmissionAccountRecord) =>
      <ActualBalanceComponent
        counterpartyGUID={record.counterparty_guid}
        accountGUID={record.wallet_account_guid}
      />,
  },
  {
    title: <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.accountName" />,
    width: 169,
    key: EEmissionAccountColumnKeys.AccountName,
    dataIndex: EEmissionAccountColumnKeys.AccountName,
  },
  {
    title: <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.issueBurnSaldo" />,
    width: 170,
    key: EEmissionAccountColumnKeys.IssuerBurnSaldo,
    dataIndex: EEmissionAccountColumnKeys.IssuerBurnSaldo,
  },
  {
    title: <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.reserveAcc" />,
    width: 149,
    key: EEmissionAccountColumnKeys.ReserveAcc,
    dataIndex: EEmissionAccountColumnKeys.ReserveAcc,
  },
  {
    title: <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.keyKeeperLogin" />,
    width: 234,
    key: EEmissionAccountColumnKeys.WalletUserExternalId,
    dataIndex: EEmissionAccountColumnKeys.WalletUserExternalId,
  },
  {
    title: <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.accountStatus" />,
    width: 246,
    key: EEmissionAccountColumnKeys.AccountStatus,
    dataIndex: EEmissionAccountColumnKeys.AccountStatus,
    render: (status: number, record) => (
      <SStatusCell>
        <Tag color={[undefined, 'green', 'volcano'][status]}>
          {
            [
              <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.accountStatus.status.closed" />,
              <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.accountStatus.status.active" />,
              <Trans i18nKey="accountManagement.counterpartyProcessing.table.column.accountStatus.status.blocked" />,
            ][status]
          }
        </Tag>
        <Dropdown
          menu={{
            items: menuItems,
            onClick: onItemActionClick?.({ item: record }),
          }}
          placement="topRight"
        >
          <Button type="text" icon={<MoreOutlined />} />
        </Dropdown>
      </SStatusCell>
    ),
  },
];
